import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Snackbar, Alert, Avatar, Divider, IconButton } from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FolderIcon from '@mui/icons-material/Folder';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';

import api from '../../api';
import SalaryDetails from './SalaryDetails'; 
import NotificationSystem from './NotificationSystem';
import EmployeeLogs from './EmployeeLogs';
import EditEmployeeForms from './EditEmployeeForms';
import EditEmployeeDocuments from './EditEmployeeDocuments';

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });
  const [photoStamp, setPhotoStamp] = useState(Date.now()); 

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]); 
  const [eduLevels, setEduLevels] = useState([]);
  const [expRanges, setExpRanges] = useState([]);
  const [skillsMaster, setSkillsMaster] = useState([]);
  const [certsMaster, setCertsMaster] = useState([]);
  const [langsMaster, setLangsMaster] = useState([]);
  
  const [documents, setDocuments] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('');

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', official_email: '',
    dob: '', gender: '', mobile: '', location: '',
    father_name: '', mother_name: '', blood_group: '', address: '',
    role: '', employee_level: '', department_id: '', designation_id: '', date_of_joining: '',
    leave_approver_l1: '', leave_approver_l2: '', status: '', shift_id: '', notice_period: '',
    education_id: '', experience_id: '', skills: [], certifications: [], languages: [],
    pan_number: '', aadhaar_number: '', uan_number: '', pf_number: '',
    bank_account_holder_name: '', bank_account: '', bank_name: '', branch_name: '', ifsc_code: '',
    ctc: '', basic: '', hra: '', special_allowance: '', gross: '', net_salary: '',
    epf_employer: '', epf_employee: '', esic_employer: '', esic_employee: '', gratuity_employer: '',
    tds: '', prof_tax: '',
    auto_basic_hra: true, auto_esic: true, auto_epf: false, auto_gratuity: true
  });

  useEffect(() => {
    Promise.all([
      api.get('/settings/departments').catch(() => ({ data: [] })),
      api.get('/settings/designations').catch(() => ({ data: [] })),
      api.get('/employees').catch(() => ({ data: [] })),
      api.get('/settings/education').catch(() => ({ data: [] })), 
      api.get('/settings/experience').catch(() => ({ data: [] })), 
      api.get('/settings/skills').catch(() => ({ data: [] })),
      api.get('/settings/certifications').catch(() => ({ data: [] })),
      api.get('/settings/languages').catch(() => ({ data: [] })),
      api.get('/settings/document_categories').catch(() => ({ data: [] }))
    ]).then(responses => {
      setDepartments(responses[0].data || []);
      setDesignations(responses[1].data || []);
      setEmployees(responses[2].data || []);
      setEduLevels(responses[3].data || []);
      setExpRanges(responses[4].data || []);
      setSkillsMaster(responses[5].data || []);
      setCertsMaster(responses[6].data || []);
      setLangsMaster(responses[7].data || []);
      setDocTypes(responses[8].data || []);
    });

    loadEmployeeData();
    loadDocuments();
    // eslint-disable-next-line
  }, [id]);

  const loadEmployeeData = async () => {
    try {
      const res = await api.get(`/employees/${id}`);
      const emp = res.data;
      
      setFormData({
        first_name: emp.first_name || '', last_name: emp.last_name || '',
        email: emp.email || '', official_email: emp.official_email || '',
        dob: emp.dob ? emp.dob.split('T')[0] : '', 
        gender: emp.gender || '', mobile: emp.mobile || '', location: emp.location || '',
        father_name: emp.father_name || '', mother_name: emp.mother_name || '', blood_group: emp.blood_group || '', address: emp.address || '',
        role: emp.role || '', employee_level: emp.employee_level || '',
        department_id: emp.department_id || '', designation_id: emp.designation_id || '', 
        date_of_joining: emp.date_of_joining ? emp.date_of_joining.split('T')[0] : '',
        leave_approver_l1: emp.leave_approver_l1 || '', leave_approver_l2: emp.leave_approver_l2 || '', 
        status: emp.status || '', shift_id: emp.shift_id || '', notice_period: emp.notice_period || '',
        education_id: emp.education_id || '', experience_id: emp.experience_id || '',
        skills: Array.isArray(emp.skills) ? emp.skills : [], 
        certifications: Array.isArray(emp.certifications) ? emp.certifications : [], 
        languages: Array.isArray(emp.languages) ? emp.languages : [],
        pan_number: emp.pan_number || '', aadhaar_number: emp.aadhaar_number || '', uan_number: emp.uan_number || '', pf_number: emp.pf_number || '',
        bank_account_holder_name: emp.bank_account_holder_name || '', bank_account: emp.bank_account || '', bank_name: emp.bank_name || '', branch_name: emp.branch_name || '', ifsc_code: emp.ifsc_code || '',
        ctc: emp.ctc || '', basic: emp.basic || '', hra: emp.hra || '', 
        special_allowance: emp.special_allowance || '', gross: emp.gross || '',
        epf_employer: emp.epf_employer || '', epf_employee: emp.epf_employee || '',
        esic_employer: emp.esic_employer || '', esic_employee: emp.esic_employee || '',
        gratuity_employer: emp.gratuity_employer || '', tds: emp.tds || '', prof_tax: emp.prof_tax || '',
        auto_basic_hra: emp.auto_basic_hra !== false, auto_esic: emp.auto_esic !== false, auto_epf: emp.auto_epf === true, auto_gratuity: emp.auto_gratuity !== false,
        net_salary: ''
      });
    } catch (err) { showMsg("Failed to load employee data", "error"); }
  };

  const loadDocuments = async () => {
    try {
      const res = await api.get(`/employees/${id}/documents`);
      setDocuments(res.data);
    } catch (err) { console.error("Docs failed to load"); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formDataObj = new FormData();
    formDataObj.append("file", file);
    try {
      await api.post(`/employees/${id}/profile-picture`, formDataObj, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoStamp(Date.now()); 
      showMsg("Profile picture updated!");
    } catch(err) { showMsg("Failed to update picture", "error"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    
    ['ctc', 'basic', 'hra', 'special_allowance', 'gross', 'epf_employer', 'epf_employee', 'esic_employer', 'esic_employee', 'gratuity_employer', 'tds', 'prof_tax', 'notice_period'].forEach(field => {
      payload[field] = (typeof payload[field] === 'string' && payload[field] !== '') ? parseFloat(payload[field]) : payload[field];
      if (payload[field] === '') payload[field] = 0;
    });

    delete payload.net_salary;
    if (payload.shift_id === '') payload.shift_id = null;
    if (payload.department_id === '') payload.department_id = null;
    if (payload.designation_id === '') payload.designation_id = null;
    if (payload.education_id === '') payload.education_id = null;
    if (payload.experience_id === '') payload.experience_id = null;

    try {
      const adminId = localStorage.getItem('emp_id');
      await api.put(`/employees/${id}?admin_id=${adminId}`, payload);
      showMsg("Profile Details Updated Successfully!");
    } catch (err) { showMsg("Update failed", "error"); }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadType) return showMsg("Select file and type", "error");
    const docData = new FormData();
    docData.append("document_type", uploadType); 
    docData.append("file", uploadFile);
    docData.append("admin_id", localStorage.getItem('emp_id'));          
    try {
      await api.post(`/employees/${id}/documents`, docData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg("Document uploaded!");
      setUploadFile(null); setUploadType(''); loadDocuments();
    } catch (err) { showMsg("Upload failed", "error"); }
  };

  const NavItem = ({ navId, label, icon }) => (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton selected={activeTab === navId} onClick={() => setActiveTab(navId)} sx={{ borderRadius: '8px', mx: 1, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' } }}>
        <ListItemIcon sx={{ minWidth: 40, color: activeTab === navId ? 'white' : 'inherit' }}>{icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: activeTab === navId ? 'bold' : 'medium' }} />
      </ListItemButton>
    </ListItem>
  );

  const isDataFormTab = ['personal', 'official', 'education', 'account', 'salary_details'].includes(activeTab);

  return (
    <Box sx={{ display: 'flex', minHeight: '85vh', mt: 2, gap: 3 }}>
      <Paper elevation={3} sx={{ width: 260, flexShrink: 0, borderRadius: 3, overflow: 'hidden', bgcolor: '#ffffff' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/employees')}>Back</Button>
        </Box>
        <Divider />
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa', position: 'relative' }}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Avatar src={`/api/v1/employees/${id}/profile-picture?t=${photoStamp}`} sx={{ width: 80, height: 80, mx: 'auto', mb: 1, bgcolor: 'primary.main', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              {formData.first_name ? formData.first_name.charAt(0) : ''}
            </Avatar>
            <IconButton component="label" size="small" sx={{ position: 'absolute', bottom: 0, right: -10, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
              <PhotoCamera sx={{ fontSize: 16 }} />
              <input hidden accept="image/*" type="file" onChange={handlePhotoUpload} />
            </IconButton>
          </Box>
          <Typography variant="subtitle1" fontWeight="bold">{formData.first_name} {formData.last_name}</Typography>
          <Typography variant="caption" color="textSecondary">{id}</Typography>
        </Box>
        <Divider />
        <List component="nav" dense>
          <NavItem navId="personal" label="Personal Details" icon={<PersonIcon fontSize="small" />} />
          <NavItem navId="official" label="Official Information" icon={<WorkIcon fontSize="small" />} />
          <NavItem navId="education" label="Education & Skills" icon={<SchoolIcon fontSize="small" />} />
          <NavItem navId="account" label="Account Details" icon={<AccountBalanceIcon fontSize="small" />} />
          <NavItem navId="salary_details" label="Salary Details" icon={<AttachMoneyIcon fontSize="small" />} />
          <NavItem navId="documents" label="Document Vault" icon={<FolderIcon fontSize="small" />} />
          <NavItem navId="notifications" label="Notification System" icon={<NotificationsActiveIcon fontSize="small" />} />
          <NavItem navId="logs" label="Activity Logs" icon={<HistoryIcon fontSize="small" />} />
        </List>
      </Paper>

      <Paper elevation={3} sx={{ flexGrow: 1, p: 4, borderRadius: 3 }}>
        {isDataFormTab ? (
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', textTransform: 'capitalize' }}>
                Edit {activeTab.replace('_', ' ')}
              </Typography>
              <Button type="submit" variant="contained" color="primary" startIcon={<SaveIcon />}>Update Profile</Button>
            </Box>
            <Divider sx={{ mb: 4 }} />

            {activeTab === 'salary_details' ? (
              <SalaryDetails formData={formData} handleChange={handleChange} setFormData={setFormData} />
            ) : (
              <EditEmployeeForms 
                activeTab={activeTab} formData={formData} handleChange={handleChange} 
                departments={departments} designations={designations} employees={employees} 
                eduLevels={eduLevels} expRanges={expRanges} skillsMaster={skillsMaster} 
                certsMaster={certsMaster} langsMaster={langsMaster} 
              />
            )}
          </Box>
        ) : activeTab === 'notifications' ? (
          <NotificationSystem employeeId={id} showMsg={showMsg} />
        ) : activeTab === 'logs' ? (
          <EmployeeLogs employeeId={id} showMsg={showMsg} />
        ) : (
          <EditEmployeeDocuments 
            documents={documents} docTypes={docTypes} uploadType={uploadType} 
            setUploadType={setUploadType} uploadFile={uploadFile} 
            setUploadFile={setUploadFile} handleDocumentUpload={handleDocumentUpload} 
          />
        )}
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default EditEmployee;